import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Cookie, Settings, Check, Shield } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Cookie Consent Banner - GDPR Compliant
 * Muestra banner de cookies en primera visita
 */
export const CookieConsentBanner = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language?.substring(0, 2) || 'es';
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Siempre activas
    analytics: false,
    marketing: false
  });

  const content = {
    es: {
      title: "Utilizamos cookies",
      description: "Usamos cookies propias y de terceros para mejorar tu experiencia, analizar el tráfico y personalizar contenido. Puedes aceptar todas, rechazar las no esenciales o configurar tus preferencias.",
      acceptAll: "Aceptar todas",
      rejectAll: "Rechazar",
      settings: "Configurar",
      save: "Guardar preferencias",
      necessary: "Cookies necesarias",
      necessaryDesc: "Esenciales para el funcionamiento del sitio. No se pueden desactivar.",
      analytics: "Cookies de análisis",
      analyticsDesc: "Nos ayudan a entender cómo usas el sitio para mejorarlo.",
      marketing: "Cookies de marketing",
      marketingDesc: "Permiten personalizar anuncios y medir campañas.",
      moreInfo: "Más información en nuestra",
      cookiePolicy: "Política de Cookies"
    },
    en: {
      title: "We use cookies",
      description: "We use our own and third-party cookies to improve your experience, analyze traffic and personalize content. You can accept all, reject non-essential ones or configure your preferences.",
      acceptAll: "Accept all",
      rejectAll: "Reject",
      settings: "Settings",
      save: "Save preferences",
      necessary: "Necessary cookies",
      necessaryDesc: "Essential for the site to work. Cannot be disabled.",
      analytics: "Analytics cookies",
      analyticsDesc: "Help us understand how you use the site to improve it.",
      marketing: "Marketing cookies",
      marketingDesc: "Allow personalized ads and campaign measurement.",
      moreInfo: "More information in our",
      cookiePolicy: "Cookie Policy"
    },
    fr: {
      title: "Nous utilisons des cookies",
      description: "Nous utilisons nos propres cookies et ceux de tiers pour améliorer votre expérience, analyser le trafic et personnaliser le contenu.",
      acceptAll: "Tout accepter",
      rejectAll: "Refuser",
      settings: "Paramètres",
      save: "Enregistrer",
      necessary: "Cookies nécessaires",
      necessaryDesc: "Essentiels au fonctionnement du site.",
      analytics: "Cookies d'analyse",
      analyticsDesc: "Nous aident à comprendre votre utilisation.",
      marketing: "Cookies marketing",
      marketingDesc: "Permettent des publicités personnalisées.",
      moreInfo: "Plus d'informations dans notre",
      cookiePolicy: "Politique de Cookies"
    }
  };

  const c = content[lang] || content.es;

  useEffect(() => {
    // Verificar si ya dio consentimiento
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) {
      // Mostrar después de un pequeño delay
      setTimeout(() => setIsVisible(true), 1500);
    }
  }, []);

  const saveConsent = (prefs) => {
    const consentData = {
      ...prefs,
      timestamp: new Date().toISOString(),
      version: '1.0'
    };
    localStorage.setItem('cookie_consent', JSON.stringify(consentData));
    
    // Aquí podrías activar/desactivar scripts según las preferencias
    if (prefs.analytics) {
      // Activar Google Analytics
      console.log('Analytics cookies enabled');
    }
    if (prefs.marketing) {
      // Activar Meta Pixel, etc.
      console.log('Marketing cookies enabled');
    }
    
    setIsVisible(false);
  };

  const acceptAll = () => {
    saveConsent({ necessary: true, analytics: true, marketing: true });
  };

  const rejectAll = () => {
    saveConsent({ necessary: true, analytics: false, marketing: false });
  };

  const savePreferences = () => {
    saveConsent(preferences);
    setShowSettings(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4" data-testid="cookie-banner">
      <div className="max-w-4xl mx-auto bg-gray-900 rounded-2xl shadow-2xl border border-gray-700 overflow-hidden">
        {!showSettings ? (
          // Banner principal
          <div className="p-4 sm:p-6">
            <div className="flex items-start gap-4">
              <div className="hidden sm:flex w-12 h-12 bg-emerald-600/20 rounded-xl items-center justify-center flex-shrink-0">
                <Cookie className="w-6 h-6 text-emerald-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
                  <Cookie className="w-5 h-5 text-emerald-400 sm:hidden" />
                  {c.title}
                </h3>
                <p className="text-gray-400 text-sm mb-4">
                  {c.description}
                </p>
                <p className="text-xs text-gray-500 mb-4">
                  {c.moreInfo}{' '}
                  <a href="/cookies" className="text-emerald-400 hover:underline">
                    {c.cookiePolicy}
                  </a>
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button 
                    onClick={acceptAll}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="accept-cookies-btn"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    {c.acceptAll}
                  </Button>
                  <Button 
                    onClick={rejectAll}
                    variant="outline"
                    className="border-gray-600 text-gray-300 hover:bg-gray-800"
                    data-testid="reject-cookies-btn"
                  >
                    {c.rejectAll}
                  </Button>
                  <Button 
                    onClick={() => setShowSettings(true)}
                    variant="ghost"
                    className="text-gray-400 hover:text-white"
                    data-testid="cookie-settings-btn"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    {c.settings}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Panel de configuración
          <div className="p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Shield className="w-5 h-5 text-emerald-400" />
                {c.settings}
              </h3>
              <button 
                onClick={() => setShowSettings(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4 mb-6">
              {/* Necesarias */}
              <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                <div className="flex-1">
                  <p className="text-white font-medium">{c.necessary}</p>
                  <p className="text-gray-400 text-sm">{c.necessaryDesc}</p>
                </div>
                <div className="w-12 h-6 bg-emerald-600 rounded-full flex items-center justify-end px-1">
                  <div className="w-4 h-4 bg-white rounded-full" />
                </div>
              </div>
              
              {/* Análisis */}
              <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                <div className="flex-1">
                  <p className="text-white font-medium">{c.analytics}</p>
                  <p className="text-gray-400 text-sm">{c.analyticsDesc}</p>
                </div>
                <button
                  onClick={() => setPreferences({...preferences, analytics: !preferences.analytics})}
                  className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                    preferences.analytics ? 'bg-emerald-600 justify-end' : 'bg-gray-600 justify-start'
                  }`}
                  data-testid="analytics-toggle"
                >
                  <div className="w-4 h-4 bg-white rounded-full" />
                </button>
              </div>
              
              {/* Marketing */}
              <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                <div className="flex-1">
                  <p className="text-white font-medium">{c.marketing}</p>
                  <p className="text-gray-400 text-sm">{c.marketingDesc}</p>
                </div>
                <button
                  onClick={() => setPreferences({...preferences, marketing: !preferences.marketing})}
                  className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                    preferences.marketing ? 'bg-emerald-600 justify-end' : 'bg-gray-600 justify-start'
                  }`}
                  data-testid="marketing-toggle"
                >
                  <div className="w-4 h-4 bg-white rounded-full" />
                </button>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button 
                onClick={savePreferences}
                className="bg-emerald-600 hover:bg-emerald-700 text-white flex-1"
                data-testid="save-cookie-prefs-btn"
              >
                {c.save}
              </Button>
              <Button 
                onClick={acceptAll}
                variant="outline"
                className="border-gray-600 text-gray-300 hover:bg-gray-800"
              >
                {c.acceptAll}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieConsentBanner;
